import { api } from 'redux/api/index.api';
import { mediaServiceApi } from 'redux/api/mediaService.api';
import Analytics from 'utils/Analytics';
import { locationType, quality } from 'utils/constants';
import { getFileUrl, getItemFilename, getItemMimeType, mimeTypes } from 'utils/file';

export const plansApi = api.injectEndpoints({
  endpoints: builder => ({
    getPlanById: builder.query({
      query: ({ projectId, planId }) => `projects/${projectId}/plans/${planId}`,
      providesTags: (result, error, { planId }) => [{ type: 'Plan', id: planId }],
    }),
    getFolderBreadcrumbs: builder.query({
      query: ({ projectId, folderId }) => `projects/${projectId}/folders/${folderId}/breadcrumbs`,
      providesTags: (result, error, { folderId }) => [{ type: 'FolderBreadcrumbs', id: folderId }],
    }),
    fetchFolderContent: builder.query({
      query: ({
        projectId,
        folderId,
        limit,
        offset,
        searchValue,
        groupId,
        withDisabled = false,
        sortPropertyName,
        sortOrder,
      }) => ({
        url: `projects/${projectId}/folder-content`,
        method: 'GET',
        params: {
          folder_id: folderId,
          limit,
          offset,
          search_name: searchValue,
          group_id: groupId,
          with_disabled: withDisabled,
          sortPropertyName,
          sortOrder,
        },
      }),
      transformResponse: (response, meta, arg) => {
        const contents = response.contents
          ?.filter(item => (arg.onlyFolders ? item.type === locationType.folder : true))
          .map(item => ({ ...item, __reorder__: item.name }));
        return {
          ...response,
          contents,
          ...(arg.onlyFolders && { count: contents?.length || 0 }),
        };
      },
      providesTags: ['Plans'],
    }),
    fetchAllPlans: builder.query({
      query: ({ projectId }) => ({
        url: `projects/${projectId}/plans`,
        method: 'GET',
      }),
      providesTags: ['Plans'],
    }),
    fetchGroupContent: builder.query({
      query: ({ projectId, groupId, includeUnshared, parentId }) => ({
        url: `projects/${projectId}/folders/group-content/${groupId}`,
        method: 'GET',
        params: {
          include_unshared: includeUnshared,
          parent_id: parentId,
        },
      }),
      providesTags: ['GroupContent'],
    }),
    fetchBim: builder.query({
      query: ({ projectId, bimId }) => ({
        url: `projects/${projectId}/plans/${bimId}/bim`,
        method: 'GET',
      }),
    }),
    deleteImage: builder.mutation({
      query: ({ projectId, imageId }) => ({
        url: `projects/${projectId}/folders/${imageId}/detach-media`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          detach_media_resource_ids: [imageId],
        }),
      }),
      invalidatesTags: ['Plans'],
    }),
    fetchZoningData: builder.query({
      queryFn: async (mediaUrl, _queryApi, _extraOptions, baseQuery) => {
        const urlResult = await baseQuery(`${mediaUrl}/url?quality=Thumbnail`);
        if (urlResult.error) return { error: urlResult.error };

        if (!urlResult.data?.media_url) {
          return { error: { status: 'CUSTOM_ERROR', error: 'No media URL found' } };
        }

        try {
          const response = await fetch(urlResult.data.media_url);
          const data = await response.json();
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: error.message } };
        }
      },
    }),
    postFolder: builder.mutation({
      query: ({ projectId, values, parentFolderId }) => ({
        url: `projects/${projectId}/folders`,
        method: 'POST',
        headers: {
          'Cache-Control': 'No-Store',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: values.name,
          id: values.id,
          parent_folder_id: parentFolderId,
        }),
      }),
      transformResponse: response => {
        Analytics.track('plans_folder_creation_succeeded');
        return response;
      },
      invalidatesTags: ['Plans'],
    }),
    fetchFolder: builder.query({
      query: ({ projectId, folderId }) => ({
        url: `projects/${projectId}/folders/${folderId}`,
        method: 'GET',
      }),
      providesTags: ['Plans'],
    }),
    fetchAllFolders: builder.query({
      query: ({ projectId, limit = 500, offset = 0 }) => ({
        url: `projects/${projectId}/folders`,
        method: 'GET',
        params: {
          limit,
          offset,
        },
      }),
      providesTags: ['Plans'],
    }),
    fetchChildrenFolders: builder.query({
      query: ({ projectId, folderId }) => ({
        url: `projects/${projectId}/folders/${folderId}/children-folders`,
        method: 'GET',
      }),
      providesTags: ['Plans'],
    }),
    downloadPlan: builder.query({
      async queryFn({ item, organizationId, projectId }, queryApi, _extraOptions, _baseQuery) {
        const rawUrl = getFileUrl(item);
        let normalizedUrl;
        let mimeType;
        if (item.type === locationType.bim) {
          normalizedUrl = `${
            window.location.origin
          }/organization/${organizationId}/project/${projectId}/${rawUrl}`;
          [mimeType] = mimeTypes.ifc;
        } else {
          normalizedUrl = rawUrl;
          mimeType = getItemMimeType(item);
        }
        try {
          const result = await queryApi
            .dispatch(
              mediaServiceApi.endpoints.download.initiate({
                url: normalizedUrl,
                quality: quality.original,
                fileName: getItemFilename(item),
                mimeType,
              }),
            )
            .unwrap();
          return { data: result };
        } catch (error) {
          return { error };
        }
      },
    }),
    createPlan: builder.mutation({
      query: ({ projectId, plan }) => ({
        url: `v1.3/projects/${projectId}/plans`,
        method: 'POST',
        body: plan,
      }),
      invalidatesTags: ['Plans'],
    }),
    uploadBimChunk: builder.mutation({
      query: ({ projectId, formData }) => ({
        url: `v1.4/projects/${projectId}/plans/bim_upload`,
        method: 'PUT',
        headers: { 'x-skip-content-type': 'true', 'Cache-Control': 'No-Store' },
        body: formData,
      }),
      invalidatesTags: ['Plans'],
    }),
    updateFolder: builder.mutation({
      query: ({ projectId, folderId, folder }) => ({
        url: `projects/${projectId}/folders/${folderId}`,
        method: 'PUT',
        body: folder,
      }),
      invalidatesTags: ['Plans', 'FolderBreadcrumbs'],
    }),
    updatePlanParentFolder: builder.mutation({
      query: ({ projectId, movedPlanIds, parentFolderId }) => ({
        url: `projects/${projectId}/plans/change-parent-folder`,
        method: 'POST',
        body: {
          moved_plan_ids: movedPlanIds,
          parent_folder_id: parentFolderId,
        },
      }),
      invalidatesTags: ['Plans', 'FolderBreadcrumbs'],
    }),
    updateFolderParentFolder: builder.mutation({
      query: ({ projectId, movedFolderIds, parentFolderId }) => ({
        url: `projects/${projectId}/folders/change-parent-folder`,
        method: 'POST',
        body: {
          moved_folder_ids: movedFolderIds,
          parent_folder_id: parentFolderId,
        },
      }),
      invalidatesTags: ['Plans', 'FolderBreadcrumbs'],
    }),
    updatePlan: builder.mutation({
      query: ({ projectId, planId, plan }) => ({
        url: `projects/${projectId}/plans/${planId}`,
        method: 'PUT',
        body: plan,
      }),
      invalidatesTags: ['Plans'],
    }),
    deleteFolder: builder.mutation({
      query: ({ projectId, folderId }) => ({
        url: `projects/${projectId}/folders/${folderId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Plans', 'FolderBreadcrumbs'],
    }),
    deletePlan: builder.mutation({
      query: ({ projectId, planId }) => ({
        url: `projects/${projectId}/plans/${planId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Plans'],
    }),
  }),
  overrideExisting: 'throw',
});

export const {
  useGetPlanByIdQuery,
  useLazyGetPlanByIdQuery,
  useGetFolderBreadcrumbsQuery,
  useLazyGetFolderBreadcrumbsQuery,
  useFetchFolderContentQuery,
  useLazyFetchFolderContentQuery,
  useFetchAllPlansQuery,
  useLazyFetchAllPlansQuery,
  useFetchZoningDataQuery,
  useDeleteImageMutation,
  usePostFolderMutation,
  useFetchGroupContentQuery,
  useLazyFetchGroupContentQuery,
  useLazyFetchBimQuery,
  useFetchFolderQuery,
  useLazyFetchFolderQuery,
  useLazyFetchAllFoldersQuery,
  useLazyFetchChildrenFoldersQuery,
  useLazyDownloadPlanQuery,
  useUpdateFolderMutation,
  useUpdatePlanParentFolderMutation,
  useUpdateFolderParentFolderMutation,
  useUpdatePlanMutation,
  useDeleteFolderMutation,
  useDeletePlanMutation,
  useCreatePlanMutation,
  useUploadBimChunkMutation,
} = plansApi;
